import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { Typography, IconButton, Collapse, Box, Snackbar } from "@mui/material";
import { ChevronDown, ChevronUp, ListTodo, List, Pencil } from "lucide-react";
import FilterListRow from "../../../Shared/Tables/FilterListRow";
import Label from "../../../Shared/Label";

function Row(props) {
  const classes = useStyles();
  const row = props.row;
  const [open, setOpen] = useState(false);

  // Close collapsible when openStates prop changes (triggered by sort/filter)
  useEffect(() => {
    if (props.openStates && !props.openStates[row.listId]) {
      setOpen(false);
    }
  }, [props.openStates, row.listId]);

  const handleToggle = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    if (props.onToggleRow) {
      props.onToggleRow(row.listId, newOpenState);
    }
  };

  const getListTypeIcon = (isMultiSelect) => {
    switch (isMultiSelect) {
      case true:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ListTodo size={16} style={{ marginRight: "8px" }} />
            Multi-select
          </div>
        );
      case false:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <List size={16} style={{ marginRight: "8px" }} />
            Single-select
          </div>
        );
      default:
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <List size={16} style={{ marginRight: "8px" }} />
            Single-select
          </div>
        );
    }
  };

  // Function to highlight search text
  const highlightText = (text, searchValue) => {
    if (!searchValue || !text || searchValue.trim() === "") return text;

    const trimmedSearchValue = searchValue.trim();
    const regex = new RegExp(`(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === trimmedSearchValue.toLowerCase()) {
        return (
          <mark key={index} style={{ backgroundColor: "#C3996B", color: "#F8F8F8", padding: "0", borderRadius: "2px" }}>
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <React.Fragment>
      <tr className={classes.table__row}>
        <td>
          <IconButton 
            style={{ padding: "4px", minWidth: "auto" }} 
            onClick={handleToggle}
            size="small"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </IconButton>
        </td>
        <td>{highlightText(row.name, props.searchValue)}</td>
        <td>{getListTypeIcon(row.isMultiSelect)}</td>
        <td>{highlightText(row.companyName, props.searchValue)}</td>
        <td className={classes.descriptionCell} title={row.description}>
          {highlightText(row.description, props.searchValue)}
        </td>
        <td>
          <div className={classes.buttonContainer}>
            <button 
              className={classes.iconButton}
              onClick={() => props.navigate && props.navigate(`/modifydropdown/${row.uniqueId}`)}
            >
              <Pencil className={classes.iconButton__icon} />
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <tr className={classes.table__row}>
          <td colSpan="6" style={{ padding: "0", border: "none" }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                  <table className={classes.innerTable}>
                    <thead>
                      <tr>
                        <th style={{ width: "40px", textAlign: "center", marginRight: "8px" }}>#</th>
                        <th style={{ paddingLeft: "24px", width: "240px" }}>Name</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.dataListItems.map((ItemRows, index) => (
                        <tr key={index}>
                          <td style={{ textAlign: "center" }}>{index + 1}</td>
                          <td style={{ paddingLeft: "24px", width: "240px" }}>{ItemRows.name}</td>
                          <td>{ItemRows.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </Box>
            </Collapse>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

// Helper component for sortable column headers
const SortableHeader = ({ children, sortKey, currentSortColumn, currentSortOrder, onSort, className = "" }) => {
  const classes = useStyles();
  const isActive = currentSortColumn === sortKey;
  
  const handleHeaderClick = () => {
    onSort(sortKey);
  };
  
  return (
    <th 
      className={`${classes.sortableHeader} ${className}`}
      onClick={handleHeaderClick}
    >
      <div className={classes.headerContent}>
        <span>{children}</span>
        <div className={classes.sortIcons}>
          <ChevronUp 
            size={14} 
            className={`${classes.sortIcon} ${isActive && currentSortOrder === 'ASC' ? classes.activeSortIcon : ''}`}
          />
          <ChevronDown 
            size={14} 
            className={`${classes.sortIcon} ${isActive && currentSortOrder === 'DESC' ? classes.activeSortIcon : ''}`}
          />
        </div>
      </div>
    </th>
  );
};

export default function TableList(props) {
  const classes = useStyles();
  const [openStates, setOpenStates] = useState({});

  // Reset all open states when sorting, filtering, or rows change
  useEffect(() => {
    setOpenStates({});
  }, [props.sortColumn, props.sortOrder, props.searchValue, props.filters, props.rows.length]);

  const handleToggleRow = (rowId, isOpen) => {
    setOpenStates(prev => ({
      ...prev,
      [rowId]: isOpen
    }));
  };
  
  return (
    <React.Fragment>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.table__header}>
            <tr>
              <th style={{ width: "48px" }}></th>
              <SortableHeader 
                sortKey="name" 
                currentSortColumn={props.sortColumn} 
                currentSortOrder={props.sortOrder} 
                onSort={props.handleSorting}
              >
                Name
              </SortableHeader>
              <SortableHeader 
                sortKey="isMultiSelect" 
                currentSortColumn={props.sortColumn} 
                currentSortOrder={props.sortOrder} 
                onSort={props.handleSorting}
                className={classes.typeHeader}
              >
                Type
              </SortableHeader>
              <SortableHeader 
                sortKey="companyName" 
                currentSortColumn={props.sortColumn} 
                currentSortOrder={props.sortOrder} 
                onSort={props.handleSorting}
              >
                Company
              </SortableHeader>
              <th>Description</th>
              <th style={{ minWidth: "16px" }} />
            </tr>
          </thead>
          <tbody className={classes.table__body}>
            {props.loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "32px" }}>
                  <div className={classes.loadingContainer}>
                    <div className={classes.spinner}></div>
                    <span>Loading dropdowns...</span>
                  </div>
                </td>
              </tr>
            ) : Array.isArray(props.rows) && props.rows.length > 0 ? (
              props.rows.map((row, index) => (
                <Row 
                  {...props} 
                  row={row} 
                  index={index} 
                  key={row.listId || index}
                  openStates={openStates}
                  onToggleRow={handleToggleRow}
                />
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "32px" }}>
                  No dropdowns found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",
    cursor: "pointer",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
    overflowY: "visible",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",
      cursor: "pointer",

      "&:first-child": {
        borderTopLeftRadius: "8px",
        cursor: "default",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200,

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  descriptionCell: {
    maxWidth: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },

  sortableHeader: {
    cursor: "pointer !important",
    userSelect: "none",
    
    "&:hover": {
      backgroundColor: "#D0D0D0 !important",
    },
  },

  typeHeader: {
    width: "128px !important",
  },

  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
  },

  sortIcons: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
  },

  sortIcon: {
    color: "#999999",
    strokeWidth: "2px",
    transition: "color 0.2s ease",
  },

  activeSortIcon: {
    color: "#4654FB !important",
  },

  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "#666666",
    fontSize: "16px",
  },

  spinner: {
    width: "20px",
    height: "20px",
    border: "2px solid #f3f3f3",
    borderTop: "2px solid #4654FB",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": { 
      transform: "rotate(0deg)" 
    },
    "100%": { 
      transform: "rotate(360deg)" 
    },
  },

  itemsContainer: {
    backgroundColor: "#F8F8F8",
    borderRadius: "4px",
    padding: "16px",
    marginTop: "8px",
  },

  innerTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",

    "& thead tr": {
      backgroundColor: "#F8F8F8",
    },

    "& th": {
      padding: "8px 16px",
      textAlign: "left",
      fontSize: "14px",
      fontWeight: "normal",
      color: "#0C0E29",
      backgroundColor: "#F8F8F8",

      "&:first-child": {
        borderTopLeftRadius: "4px",
        borderBottomLeftRadius: "4px",
      },

      "&:last-child": {
        borderTopRightRadius: "4px",
        borderBottomRightRadius: "4px",
      },
    },

    "& tbody tr": {
      border: "none",
      backgroundColor: "transparent",

      "&:nth-child(even)": {
        backgroundColor: "transparent",
      },

      "&:last-child": {
        borderBottom: "none",
      },
    },

    "& td": {
      padding: "8px 16px",
      fontSize: "14px",
      color: "#0C0E29",
      border: "none",
      borderRight: "none !important",
    },
  },
}));
